import { Box, Button, HStack, Text } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type UserProfitReportGroupedByQuery,
  UtilizationReportDataSource,
  UtilizationReportGroupBy,
  UtilizationReportGroupOrderByColumn,
} from "@src/__generated__/graphql";
import type { UserProfitReportStore } from "@src/components/modules/reports/user-profit-report/store";
import {
  AvailableCapacityTooltip,
  TrackedAndAllocatedTooltip,
  userCell,
} from "@src/components/modules/reports/utilization/UtilizationReport";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  LoadingOverlay,
} from "@src/components/ui-kit";
import { RangeProgressBar } from "@src/components/ui-kit/ProgressBar/RangeProgressBar";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { formatSecondsToHours } from "@src/utils/time";
import type { CellContext } from "@tanstack/react-table";
import { differenceInMonths } from "date-fns";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { now } from "mobx-utils";
import { useMemo } from "react";

type UserProfitReportGroupedByData =
  UserProfitReportGroupedByQuery["utilizationReportGroups"]["data"][0];

export type UserProfitReportGroupedByRow = {
  id: string;
} & UserProfitReportGroupedByData & {
    __subRows: UserProfitReportGroupedBySubrow[];
  };

type UserProfitReportGroupedBySubrow = {
  id: string;
  items: UserProfitReportGroupedByData["items"];
} & Pick<UserProfitReportGroupedByData["items"][0], "report" | "user">;

type UserProfitReportGroupedTableRow =
  | {
      id: string;
      rowType: "group";
      group_title: UserProfitReportGroupedByData["group_title"];
      report: UserProfitReportGroupedByData["report"];
      items: UserProfitReportGroupedByData["items"];
      __subRows?: UserProfitReportGroupedTableRow[];
    }
  | {
      id: string;
      rowType: "item";
      group_title: UserProfitReportGroupedByData["group_title"];
      report: UserProfitReportGroupedByData["items"][0]["report"];
      user: UserProfitReportGroupedByData["items"][0]["user"];
      items: UserProfitReportGroupedByData["items"];
    };

type UserProfitReportGroupedByTableProps = {
  store: UserProfitReportStore;
};

type UserProfitReportGroupedCellContext = CellContext<
  UserProfitReportGroupedTableRow,
  unknown
>;

const isGroupRow = (
  row: UserProfitReportGroupedTableRow,
): row is Extract<UserProfitReportGroupedTableRow, { rowType: "group" }> =>
  row.rowType === "group";

export const UserProfitReportGroupedByTable = observer(
  function UserProfitReportGroupedByTable({
    store,
  }: UserProfitReportGroupedByTableProps) {
    const { workspaceStore } = useStore();

    const columns = useMemo<
      | BodyColumnDef<
          UserProfitReportGroupedTableRow,
          UtilizationReportGroupOrderByColumn
        >[]
      | undefined
    >(() => {
      const isDateInPast = computed(() => {
        return differenceInMonths(store.dateRange.start, now()) < 0;
      }).get();

      const positionCol: BodyColumnDef<
        UserProfitReportGroupedTableRow,
        UtilizationReportGroupOrderByColumn
      > = {
        id: "position",
        meta: { label: t`Position` },
        header: () => t`Position`,
        cell: ({ row }) =>
          isGroupRow(row.original) ? null : (
            <span>{row.original.user.profile?.defaultWorktype.title}</span>
          ),
      };

      const teamCol: BodyColumnDef<
        UserProfitReportGroupedTableRow,
        UtilizationReportGroupOrderByColumn
      > = {
        id: "team",
        meta: { label: t`Team` },
        header: () => t`Team`,
        cell: ({ row }) =>
          isGroupRow(row.original) ? null : (
            <span>{row.original.user.team?.name}</span>
          ),
      };

      const sourceColVariants: Record<
        UtilizationReportDataSource,
        BodyColumnDef<
          UserProfitReportGroupedTableRow,
          UtilizationReportGroupOrderByColumn
        >
      > = {
        [UtilizationReportDataSource.TimeTracking]: {
          id: "tracked",
          meta: { label: t`Tracked` },
          header: () => t`Tracked`,
          orderBy: UtilizationReportGroupOrderByColumn.TrackedTime,
          cell: ({ row }) => (
            <span>
              {formatSecondsToHours(row.original.report.tracked_time ?? 0, 1)}
            </span>
          ),
        },
        [UtilizationReportDataSource.Planning]: {
          id: "allocated",
          meta: { label: t`Allocated` },
          header: () => t`Allocated`,
          orderBy: UtilizationReportGroupOrderByColumn.AllocatedTime,
          cell: ({ row }) => (
            <span>
              {formatSecondsToHours(row.original.report.allocated_time ?? 0, 1)}
            </span>
          ),
        },
        [UtilizationReportDataSource.TaskPositions]: {
          id: "task-budgeted",
          meta: { label: t`Task budgeted` },
          header: () => t`Task budgeted`,
          orderBy: UtilizationReportGroupOrderByColumn.BudgetedTime,
          cell: ({ row }) => (
            <span>
              {formatSecondsToHours(row.original.report.budgeted_time ?? 0, 1)}
            </span>
          ),
        },
        [UtilizationReportDataSource.Combined]: {
          id: "tracked+allocated",
          meta: { label: t`Tracked + Allocated` },
          header: () => t`Tracked + Allocated`,
          orderBy: UtilizationReportGroupOrderByColumn.CombinedTiMe,
          cell: ({ row }) => (
            <TrackedAndAllocatedTooltip
              row={row.original}
              date={store.dateRange.start}
            >
              <span>
                {formatSecondsToHours(
                  row.original.report.combined_time ?? 0,
                  1,
                )}
              </span>
            </TrackedAndAllocatedTooltip>
          ),
        },
      };

      const sourceCol = sourceColVariants[store.dataSource];
      const billableCol: BodyColumnDef<
        UserProfitReportGroupedTableRow,
        UtilizationReportGroupOrderByColumn
      > = {
        id: "billable",
        meta: { label: t`Billable` },
        header: () => t`Billable`,
      };

      switch (store.dataSource) {
        case UtilizationReportDataSource.TimeTracking:
          billableCol.orderBy =
            UtilizationReportGroupOrderByColumn.TrackedTimeBillable;
          billableCol.cell = ({ row }) => (
            <span>
              {formatSecondsToHours(
                row.original.report.tracked_time_billable ?? 0,
                1,
              )}
            </span>
          );
          break;
        case UtilizationReportDataSource.Planning:
          billableCol.orderBy =
            UtilizationReportGroupOrderByColumn.AllocatedTimeBillable;
          billableCol.cell = ({ row }) => (
            <span>
              {formatSecondsToHours(
                row.original.report.allocated_time_billable ?? 0,
                1,
              )}
            </span>
          );
          break;
        case UtilizationReportDataSource.TaskPositions:
          billableCol.orderBy =
            UtilizationReportGroupOrderByColumn.BudgetedTimeBillable;
          billableCol.cell = ({ row }) => (
            <span>
              {formatSecondsToHours(
                row.original.report.budgeted_time_billable ?? 0,
                1,
              )}
            </span>
          );
          break;
        case UtilizationReportDataSource.Combined:
          billableCol.orderBy =
            UtilizationReportGroupOrderByColumn.CombinedTimeBillable;
          billableCol.cell = ({ row }) => (
            <span>
              {formatSecondsToHours(
                row.original.report.combined_time_billable ?? 0,
                1,
              )}
            </span>
          );
          break;
      }

      const isTaskPositions =
        store.dataSource === UtilizationReportDataSource.TaskPositions;

      const baseColumns: BodyColumnDef<
        UserProfitReportGroupedTableRow,
        UtilizationReportGroupOrderByColumn
      >[] = [
        {
          id: "grouped-by",
          meta: { label: String(store.activeGroupByOption?.label ?? "") },
          header: () => store.activeGroupByOption?.label,
          orderBy: UtilizationReportGroupOrderByColumn.Group,
          enableHiding: false,
          cell: ({ row }: UserProfitReportGroupedCellContext) => {
            if (isGroupRow(row.original)) {
              return <span>{row.original.group_title}</span>;
            }

            const { user } = row.original;

            return userCell(user, () =>
              store.userModalStore.modalState.onOpen({
                id: user.id,
                dataSource: store.dataSource,
                dateRange: store.dateRange,
              }),
            );
          },
        },
        {
          id: "available-capacity",
          meta: { label: t`Available Capacity` },
          header: () => t`Available Capacity`,
          orderBy: UtilizationReportGroupOrderByColumn.AvailableTime,
          cell: ({ row }: UserProfitReportGroupedCellContext) => (
            <AvailableCapacityTooltip row={row.original}>
              <span>
                {formatSecondsToHours(row.original.report.available_time, 1)}
              </span>
            </AvailableCapacityTooltip>
          ),
        },
        sourceCol,
        billableCol,
        {
          id: "tracked-time-billable",
          meta: { label: t`Tracked time` },
          header: () => t`Tracked time`,
          cell: ({ row }: UserProfitReportGroupedCellContext) =>
            formatSecondsToHours(
              row.original.report.tracked_time_billable ?? 0,
              1,
            ),
        },
        {
          id: "allocated-time-billable",
          meta: { label: t`Allocated time` },
          header: () => t`Allocated time`,
          cell: ({ row }: UserProfitReportGroupedCellContext) =>
            formatSecondsToHours(
              row.original.report.allocated_time_billable ?? 0,
              1,
            ),
        },
        {
          id: "left-to-allocate",
          meta: { label: isDateInPast ? t`Unused hours` : t`Left to allocate` },
          header: () => (isDateInPast ? t`Unused hours` : t`Left to allocate`),
          cell: ({ row }: UserProfitReportGroupedCellContext) =>
            row.original.report.remaining_time > 0 ? (
              <span>
                {formatSecondsToHours(row.original.report.remaining_time, 1)}
              </span>
            ) : null,
        },
        {
          id: "overtime",
          meta: { label: t`Overtime` },
          header: () => t`Overtime`,
          cell: ({ row }: UserProfitReportGroupedCellContext) =>
            row.original.report.overtime > 0 ? (
              <span>
                {formatSecondsToHours(row.original.report.overtime, 1)}
              </span>
            ) : null,
        },
        {
          id: "revenue-estimate",
          meta: { label: t`Revenue estimate` },
          header: () => t`Revenue estimate`,
          cell: ({ row }: UserProfitReportGroupedCellContext) =>
            currency.formatByWorkspace(
              store.dataSource === UtilizationReportDataSource.Combined
                ? (row.original.report.combined_amount_billable ?? 0)
                : (row.original.report.budgeted_amount_billable ?? 0),
            ),
        },
        {
          id: "internal-cost",
          meta: { label: t`Internal cost` },
          header: () => t`Internal cost`,
          cell: ({ row }: UserProfitReportGroupedCellContext) =>
            currency.formatByWorkspace(row.original.report.internal_cost ?? 0),
        },
        {
          id: "combined-profit-amount",
          meta: { label: t`User profit` },
          header: () => t`User profit`,
          cell: ({ row }: UserProfitReportGroupedCellContext) => {
            const amount =
              store.dataSource === UtilizationReportDataSource.Combined
                ? (row.original.report.combined_profit_amount ?? 0)
                : (row.original.report.budgeted_profit_amount ?? 0);

            return (
              <Text color={amount < 0 ? "red.500" : undefined}>
                {currency.formatByWorkspace(amount)}
              </Text>
            );
          },
        },
        {
          id: "utilization",
          meta: { label: t`Utilization` },
          header: () => t`Utilization`,
          orderBy: UtilizationReportGroupOrderByColumn.UtilizationPercentage,
          cell: ({ row }: UserProfitReportGroupedCellContext) => (
            <HStack>
              <RangeProgressBar
                w="96px"
                progress={row.original.report.utilization_percentage ?? 0}
                exceededThreshold={{
                  below: isGroupRow(row.original)
                    ? (workspaceStore.settings
                        ?.default_utilization_range_from ?? 80)
                    : (row.original.user.profile?.utilization_range_from ?? 0),
                  above: isGroupRow(row.original)
                    ? (workspaceStore.settings?.default_utilization_range_to ??
                      100)
                    : (row.original.user.profile?.utilization_range_to ?? 0),
                }}
              />
              <Text minW="10" textAlign="end">
                {row.original.report.utilization_percentage ?? 0}%
              </Text>
            </HStack>
          ),
        },
        {
          id: "billable-utilization",
          meta: { label: t`Billable utilization` },
          header: () => t`Billable utilization`,
          orderBy:
            UtilizationReportGroupOrderByColumn.UtilizationPercentageBillable,
          cell: ({ row }: UserProfitReportGroupedCellContext) => (
            <HStack>
              <RangeProgressBar
                w="96px"
                progress={
                  row.original.report.utilization_percentage_billable ?? 0
                }
                exceededThreshold={{
                  below: isGroupRow(row.original)
                    ? (workspaceStore.settings
                        ?.default_utilization_range_from ?? 80)
                    : (row.original.user.profile?.utilization_range_from ?? 0),
                  above: isGroupRow(row.original)
                    ? (workspaceStore.settings?.default_utilization_range_to ??
                      100)
                    : (row.original.user.profile?.utilization_range_to ?? 0),
                }}
              />
              <Text minW="10" textAlign="end">
                {row.original.report.utilization_percentage_billable ?? 0}%
              </Text>
            </HStack>
          ),
        },
        {
          id: ACTIONS_COLUMN_ID,
          meta: { label: t`Actions` },
          header: () => t`Actions`,
          cell: ({ row }: UserProfitReportGroupedCellContext) => {
            if (isGroupRow(row.original)) {
              return null;
            }

            const { user } = row.original;

            return (
              <Button
                fontWeight="medium"
                onClick={() => {
                  store.userModalStore.modalState.onOpen({
                    id: user.id,
                    dataSource: store.dataSource,
                    dateRange: store.dateRange,
                  });
                }}
                role="link"
                size="xs"
                variant="link"
              >
                <Trans>Detail</Trans>
              </Button>
            );
          },
        },
      ].filter((column) => {
        if (isTaskPositions) {
          return ![
            "allocated-time-billable",
            "left-to-allocate",
            "overtime",
          ].includes(column.id as string);
        }

        return true;
      });

      switch (store.groupBy) {
        case UtilizationReportGroupBy.Month:
        case UtilizationReportGroupBy.Week:
          return [
            ...baseColumns.slice(0, 1),
            teamCol,
            positionCol,
            ...baseColumns.slice(1),
          ];
        case UtilizationReportGroupBy.WorkType:
          return [...baseColumns.slice(0, 1), teamCol, ...baseColumns.slice(1)];
        case UtilizationReportGroupBy.Team:
          return [
            ...baseColumns.slice(0, 1),
            positionCol,
            ...baseColumns.slice(1),
          ];
        default:
          return undefined;
      }
    }, [
      store.activeGroupByOption?.label,
      store.dataSource,
      store.dateRange,
      store.groupBy,
      store.userModalStore.modalState,
      workspaceStore.settings?.default_utilization_range_from,
      workspaceStore.settings?.default_utilization_range_to,
    ]);

    const data = useMemo<UserProfitReportGroupedTableRow[]>(
      () =>
        store.tableGroupedByData.map(
          ({ group_title, report, items }, index) => ({
            id: index.toString(),
            rowType: "group",
            group_title,
            report,
            items,
            __subRows: items.map(({ report: itemReport, user }, itemIndex) => ({
              id: `${index}-${itemIndex}`,
              rowType: "item",
              group_title,
              report: itemReport,
              user,
              items,
            })),
          }),
        ),
      [store.tableGroupedByData],
    );

    return (
      <Box pos="relative">
        <LoadingOverlay
          isLoading={store.isReportGroupedByLoading}
          position="absolute"
        >
          <DataTable.Body<
            UserProfitReportGroupedTableRow,
            UtilizationReportGroupOrderByColumn
          >
            tableKey={store.moduleKey}
            data={data}
            columns={columns ?? []}
            orderedBy={store.activeOrderByGrouped}
            onOrderByChange={store.handleChangeOrderByGrouped}
          />
        </LoadingOverlay>
      </Box>
    );
  },
);
